import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AddCircleOutline,
  ChevronRight,
  Close,
  ExpandMore,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import apiService from "../../../service/Api";
import { tokens } from "../../../theme";
import type { IBrandModel, ICategory, PaginatedResponse } from "../../../types/equipment";


interface EditCatalogDialogProps {
  open: boolean;
  onClose: () => void;
  brandModel: IBrandModel;
  onSaved: (updated: IBrandModel) => void;
}

interface FormState {
  brand: string;
  model: string;
  category_id: string;
  manufacturer: string;
  description: string;
  rental_value: string;
  length: string;
  height: string;
  width: string;
  weight: string;
  measuring_unit: string;
  power_draw: string;
  current_draw: string;
}


export default function EditCatalogDialog({
  open,
  onClose,
  brandModel,
  onSaved,
}: EditCatalogDialogProps) {
  const theme  = useTheme();
  const colors = tokens(theme.palette.mode);

  const [form,           setForm]           = useState<FormState>(toForm(brandModel));
  const [categories,     setCategories]     = useState<ICategory[]>([]);
  const [errors,         setErrors]         = useState<Record<string, string>>({});
  const [saving,         setSaving]         = useState(false);
  const [showSpecs,      setShowSpecs]      = useState(false);
  const [addCatOpen,     setAddCatOpen]     = useState(false);
  const [newCat,         setNewCat]         = useState({ name: "", parent: "" });
  const [savingCat,      setSavingCat]      = useState(false);
  const [apiError,       setApiError]       = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(toForm(brandModel));
      setErrors({});
      setApiError(null);
      setShowSpecs(false);
    }
  }, [open, brandModel]);

  useEffect(() => {
    apiService
      .get<PaginatedResponse<ICategory>>("/api/equipments/categories/")
      .then((res) => setCategories(res.data.results))
      .catch(() => {});
  }, []);


  const set = (field: keyof FormState, value: string) => {
    setForm((p) => ({ ...p, [field]: value }));
    setErrors((p) => ({ ...p, [field]: "" }));
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.model.trim())    e.model    = "Model is required";
    if (!form.category_id)     e.category_id = "Category is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };


  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setApiError(null);
    try {
      const payload = {
        model:         form.model       || undefined,
        manufacturer:  form.manufacturer || undefined,
        description:   form.description  || undefined,
        category_id:   form.category_id  || undefined,
        rental_value:  form.rental_value || undefined,
        length:        form.length       || undefined,
        height:        form.height       || undefined,
        width:         form.width        || undefined,
        weight:        form.weight       || undefined,
        measuring_unit: form.measuring_unit || "cm",
        power_draw:    form.power_draw   || undefined,
        current_draw:  form.current_draw || undefined,
      };

      const res = await apiService.patch<IBrandModel>(
        `/api/equipments/brand-model/${brandModel.id}/`,
        payload
      );
      onSaved(res.data);
      onClose();
        } catch (err: unknown) {
        const flat = (o: unknown): string[] => {
          if (typeof o === "string") return [o];
          if (Array.isArray(o)) return o.flatMap(flat);
          if (o && typeof o === "object") return Object.values(o as Record<string, unknown>).flatMap(flat);
          return [];
        };
        const msgs = flat((err as { response?: { data?: unknown } })?.response?.data);
        setApiError(msgs.length ? msgs.join(" · ") : "Failed to save. Please try again.");
      } finally {
        setSaving(false);
      }
  };

  const handleSaveCat = async () => {
    if (!newCat.name.trim()) return;
    setSavingCat(true);
    try {
      const res = await apiService.post<ICategory>("/api/equipments/categories/", {
        name: newCat.name,
        ...(newCat.parent ? { parent: newCat.parent } : {}),
      });
      setCategories((p) => [...p, res.data]);
      set("category_id", res.data.id.toString());
      setAddCatOpen(false);
      setNewCat({ name: "", parent: "" });
    } catch (e) {
      console.error(e);
    }
    finally { setSavingCat(false); }
  };


  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: colors.primary[800],
      borderRadius: "16px",
      "& fieldset": { border: "none" },
    },
  };

  const labelSx = {
    fontWeight: 600,
    color: colors.primary[400],
    mb: 0.5,
    fontSize: "0.88rem",
  };

  const errSx = { fontSize: "0.75rem", color: "error.main", mt: 0.5 };


  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 5, overflow: "hidden", maxHeight: "90vh" } }}
      >
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 10, top: 10, zIndex: 10, color: colors.primary[400] }}
        >
          <Close sx={{ fontSize: 18 }} />
        </IconButton>

        <DialogContent sx={{ pt: 4, pb: 3, px: 4 }}>
          <Typography variant="h6" fontWeight={700} color={colors.primary[400]} mb={0.5}>
            Edit Catalog Entry
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {brandModel.brand_name
              ? `${brandModel.brand_name} ${brandModel.model}`
              : brandModel.model}
          </Typography>

          {apiError && (
            <Typography sx={{ fontSize: 13, color: "error.main", mb: 2 }}>{apiError}</Typography>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

            <Box>
              <Typography sx={labelSx}>Brand</Typography>
              <TextField
                fullWidth
                size="small"
                value={form.brand}
                disabled
                sx={inputSx}
                helperText="Brand cannot be changed here. Create a new catalog entry instead."
                FormHelperTextProps={{ sx: { fontSize: 11 } }}
              />
            </Box>

            {/* Model */}
            <Box>
              <Typography sx={labelSx}>Model *</Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. FX6"
                value={form.model}
                onChange={(e) => set("model", e.target.value)}
                error={!!errors.model}
                sx={inputSx}
              />
              {errors.model && <Typography sx={errSx}>{errors.model}</Typography>}
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <Box>
                <Typography sx={labelSx}>Category *</Typography>
                <FormControl fullWidth size="small" sx={inputSx} error={!!errors.category_id}>
                  <Select
                    displayEmpty
                    value={form.category_id}
                    onChange={(e) => set("category_id", e.target.value)}
                    renderValue={(v) =>
                      v
                        ? (categories.find((c) => c.id.toString() === v)?.name ?? v)
                        : <Typography sx={{ color: "#A0938D" }}>Select category</Typography>
                    }
                  >
                    {categories.map((c) => (
                      <MenuItem key={c.id} value={c.id.toString()}>
                        {c.parent_name ? `${c.parent_name} › ${c.name}` : c.name}
                      </MenuItem>
                    ))}
                    <MenuItem
                      onClick={(e) => { e.stopPropagation(); setAddCatOpen(true); }}
                      sx={{ borderTop: `1px solid ${colors.primary[700]}`, mt: 0.5, pt: 1 }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: colors.primary[400] }}>
                        <AddCircleOutline sx={{ fontSize: 16 }} />
                        <Typography fontSize={13} fontWeight={500}>Add New Category</Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
                {errors.category_id && <Typography sx={errSx}>{errors.category_id}</Typography>}
              </Box>

              <Box>
                <Typography sx={labelSx}>Rental Value / day</Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  placeholder="e.g. 50"
                  value={form.rental_value}
                  onChange={(e) => set("rental_value", e.target.value)}
                  sx={inputSx}
                />
              </Box>
            </Box>

            {/* Manufacturer */}
            {/* <Box>
              <Typography sx={labelSx}>Manufacturer</Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Sony Corporation"
                value={form.manufacturer}
                onChange={(e) => set("manufacturer", e.target.value)}
                sx={inputSx}
              />
            </Box> */}

            {/* Description */}
            <Box>
              <Typography sx={labelSx}>Description</Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="Optional notes about this model..."
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                sx={inputSx}
              />
            </Box>

            <Box>
              <Box
                onClick={() => setShowSpecs((p) => !p)}
                sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", userSelect: "none", color: colors.primary[400] }}
              >
                {showSpecs
                  ? <ExpandMore sx={{ fontSize: 18 }} />
                  : <ChevronRight sx={{ fontSize: 18 }} />}
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>
                  Technical specs (dimensions, power draw)
                </Typography>
              </Box>

              <Collapse in={showSpecs}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 2 }}>
                    {(["length", "height", "width", "weight"] as const).map((f) => (
                      <Box key={f}>
                        <Typography sx={labelSx} textTransform="capitalize">{f}</Typography>
                        <TextField
                          fullWidth size="small" type="number"
                          placeholder={f === "weight" ? "kg" : "cm"}
                          value={form[f]}
                          onChange={(e) => set(f, e.target.value)}
                          sx={inputSx}
                        />
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                    <Box>
                      <Typography sx={labelSx}>Power Draw</Typography>
                      <TextField fullWidth size="small" placeholder="W" value={form.power_draw} onChange={(e) => set("power_draw", e.target.value)} sx={inputSx} />
                    </Box>
                    <Box>
                      <Typography sx={labelSx}>Current Draw</Typography>
                      <TextField fullWidth size="small" placeholder="A" value={form.current_draw} onChange={(e) => set("current_draw", e.target.value)} sx={inputSx} />
                    </Box>
                  </Box>
                </Box>
              </Collapse>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 1 }}>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{ borderRadius: "12px", px: 3, fontWeight: 600, borderColor: colors.primary[700], color: "text.primary" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{ borderRadius: "12px", px: 4, fontWeight: 600, backgroundColor: colors.primary[400], color: colors.primary[900] }}
              >
                {saving ? <CircularProgress size={18} color="inherit" /> : "Save Changes"}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={addCatOpen}
        onClose={() => setAddCatOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, backgroundColor: colors.primary[900] } }}
      >
        <IconButton onClick={() => setAddCatOpen(false)} sx={{ position: "absolute", right: 14, top: 14 }}>
          <Close sx={{ fontSize: 14 }} />
        </IconButton>
        <DialogContent sx={{ p: "24px" }}>
          <Typography variant="h6" fontWeight={600} color={colors.primary[400]} mb={3}>
            Add New Category
          </Typography>
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary" mb={1}>Category Name *</Typography>
            <TextField fullWidth placeholder="e.g. Cameras" value={newCat.name} onChange={(e) => setNewCat((p) => ({ ...p, name: e.target.value }))} size="small" sx={inputSx} />
          </Box>
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary" mb={1}>Parent Category (optional)</Typography>
            <FormControl fullWidth size="small" sx={inputSx}>
              <Select
                displayEmpty
                value={newCat.parent}
                onChange={(e) => setNewCat((p) => ({ ...p, parent: e.target.value }))}
                renderValue={(v) => v ? (categories.find((c) => c.id.toString() === v)?.name ?? v) : <Typography sx={{ color: "#A0938D" }}>None (top level)</Typography>}
              >
                <MenuItem value="">None (top level)</MenuItem>
                {categories.map((c) => <MenuItem key={c.id} value={c.id.toString()}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              variant="contained"
              onClick={handleSaveCat}
              disabled={savingCat || !newCat.name.trim()}
              sx={{ backgroundColor: colors.primary[400], color: colors.primary[900], borderRadius: "12px", px: 4 }}
            >
              {savingCat ? <CircularProgress size={16} color="inherit" /> : "Save Category"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}


function toForm(bm: IBrandModel): FormState {
  return {
    brand:          bm.brand_name    || "",
    model:          bm.model         || "",
    category_id:
      bm.category && typeof bm.category === "object"
        ? String((bm.category as { id: number }).id)
        : "",
    manufacturer:   bm.manufacturer  || "",
    description:    bm.description   || "",
    rental_value:   bm.rental_value  || "",
    length:         bm.length        || "",
    height:         bm.height        || "",
    width:          bm.width         || "",
    weight:         bm.weight        || "",
    measuring_unit: bm.measuring_unit || "cm",
    power_draw:     bm.power_draw    || "",
    current_draw:   bm.current_draw  || "",
  };
}